import axios from 'axios';
import Utility from 'classes/Utility';
import RequestQueue from 'classes/RequestQueue';
import EventManager from './EventManager';


let isPremium = null;

class DataExport {
  static dataExportInstance = null;

  constructor() {
    this.CONFIG_API_URL      = '/api/configuration/';
    this.API_URL             = '/api/data_export/';
    this.MITRE_TABLE_API_URL = '/api/mitre_table_data_export/';
    this.WATCHLIST_API_URL   = '/api/watchlist_data_export/';
    this.PAYMENT_API_URL     = '/api/dividend/stock_export/payments/';
    this.QUOTE_API_URL       = '/api/dividend/stock_export/quotes/';
    this.RECOVERY_API_URL    = '/api/dividend/stock_export/recoveries/';
  }

  static instance() {
    if (!DataExport.dataExportInstance) {
      DataExport.dataExportInstance = new DataExport();
    }
    return DataExport.dataExportInstance;
  }

  init = async () => {
    const currentUserData = await this._getCurrentUser();

    // This is wrong, bc/ user generates events (bia button clicks through Stimulus controllers)
    // and doesn't see any feedback. That UX feels far from great.
    // TODO (michael.g): Rework this into a utility and start export directly from controllers.
    // See example of downloading the tables exports.
    if (!currentUserData.premium) return;

    EventManager.on('start_export', data => {
      this.startExport(data.values);
    });

    EventManager.on('start_payment_export', data => {
      this.startPaymentExport(data.values);
    });

    EventManager.on('start_quote_export', data => {
      this.startQuoteExport(data.values);
    });

    EventManager.on('start_recovery_export', data => {
      this.startRecoveryExport(data.values);
    });

    EventManager.on('check_export', data => {
      this.checkExport(data.values);
    });

    EventManager.on('download_export', data => {
      this.downloadURI(data.values.file_url);
      this.archiveExport(data.values.file_name);
    });

    EventManager.on('start_watchlist_export', data => {
      this.startWatchlistExport(data.values);
    });
  }

  async _ensureCurrentUserIsPremium() {
    if (isPremium === null) {
      try {
        const currentUserData = await this._getCurrentUser();
        isPremium = currentUserData.premium
      } catch (err) {
        throw err;
      }
    }

    if (!isPremium) {
      throw new Error('Current user does not have a premium subscription!');
    };
  }

  async _getCurrentUser() {
    try {
      const response = await RequestQueue.addToQueue({
        url: this.CONFIG_API_URL,
        method: 'post',
        data: {
          type: 'current_user'
        },
        cached: true
      });

      return response.data;
    } catch (err) {
      throw err;
    }
  }

  startExport = (params) => {
    axios.post(this.API_URL, params).then((res) => {
      this.addExportToCookie(res.data.file_name, res.data.file_url);
      this.setPendingMessage();
    }).catch((e) => {
      return;
    });
  };

  startMitreTableExport = async (params) => {
    try {
      await this._ensureCurrentUserIsPremium();
      const res = await axios.post(this.MITRE_TABLE_API_URL, params);
      this.addExportToCookie(res.data.file_name, res.data.file_url);
      this.setPendingMessage();
    } catch (err) {
      throw err;
    }
  }

  startQuoteExport = slug => {
    axios.post(this.QUOTE_API_URL + slug + '/').then((res) => {
      this.addExportToCookie(res.data.file_name, res.data.file_url);
      this.setPendingMessage();
    }).catch(() => {
      return;
    });
  }

  startRecoveryExport = slug => {
    axios.post(this.RECOVERY_API_URL + slug + '/').then((res) => {
      this.addExportToCookie(res.data.file_name, res.data.file_url);
      this.setPendingMessage();
    }).catch(() => {
      return;
    });
  }

  startPaymentExport = slug => {
    axios.post(this.PAYMENT_API_URL + slug + '/').then((res) => {
      this.addExportToCookie(res.data.file_name, res.data.file_url);
      this.setPendingMessage();
    }).catch(() => {
      return;
    });
  }

  startWatchlistExport = params => {
    axios.post(this.WATCHLIST_API_URL, params).then((res) => {
      this.addExportToCookie(res.data.file_name, res.data.file_url);
      EventManager.emit('flash-message', {
        status: 'SUCCESS',
        title: 'Your file will start to download shortly'
      });
    }).catch(() => {
      return;
    });
  }

  checkExport = callback => {
    axios.get(this.API_URL).then((res) => {
      callback(res.data);
    });
  }

  archiveExport = file_name => {
    axios.delete(this.API_URL + file_name + '/').then(() => {
      this.deleteExportFromCookie(file_name);
    });
  }

  addExportToCookie = (file_name, file_url) => {
    const cookie_name = 'export_files';
    var files = Utility.getCookie(cookie_name);

    if (files == 'unknown') {
      files = [[file_name, file_url]];
    } else {
      files = JSON.parse(files);
      files.push([file_name, file_url]);
    }

    var expire = new Date();
    expire.setTime(expire.getTime() + 1 * 3600 * 1000);
    Utility.setCookieWithExpiration(cookie_name, JSON.stringify(files), expire.toUTCString())
  }

  deleteExportFromCookie = file_name => {
    const cookie_name = 'export_files';
    var files = Utility.getCookie(cookie_name);

    if (files == 'unknown') {
      files = []
    } else {
      files = JSON.parse(files).filter(function (el) { return el[0] != file_name })
    }

    var expire = new Date();
    expire.setTime(expire.getTime() + 1 * 3600 * 1000);

    Utility.setCookieWithExpiration(cookie_name, JSON.stringify(files), expire.toUTCString())
  }

  downloadURI = uri => {
    EventManager.emit('flash-message', {
      status: 'SUCCESS',
      title: 'Your download is ready'
    });
    const downloadIframe = document.createElement("iframe");
    document.body.appendChild(downloadIframe);
    downloadIframe.classList.add('t-hidden');
    downloadIframe.src = uri;
  }

  setPendingMessage = () => {
    EventManager.emit('flash-message', {
      status: 'PENDING',
      title: 'Preparing your download...',
      dslName: 'ConfirmationMessageTableDownload'
    });
  }
}

export default DataExport.instance();
